import { useEffect } from 'react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import { MtTooltip } from '@components/MtTooltip';
import { useAssetManager } from './useAssetManager';
import { ImportDropZone } from './ImportDropZone';
import { AssetCardGrid } from './AssetCardGrid';
import { TransformEditor } from './TransformEditor';
import { GizmoToolbar } from './GizmoToolbar';
import type { RendererController } from '@renderer/index';

interface AssetPanelProps {
  getController: () => RendererController | null;
}

export function AssetPanel({ getController }: AssetPanelProps) {
  const { t } = useTranslation();
  const panelOpen = useAssetLibraryStore((s) => s.panelOpen);
  const setPanelOpen = useAssetLibraryStore((s) => s.setPanelOpen);
  const selectedAssetId = useAssetLibraryStore((s) => s.selectedAssetId);
  const gizmoMode = useAssetLibraryStore((s) => s.gizmoMode);
  const {
    importFile,
    importGltfFiles,
    importUrl,
    removeAsset,
    duplicateAsset,
    setTransform,
    rename,
    bindModelEvents,
  } = useAssetManager(getController);

  // 绑定 viewer model 事件，同步到 store
  useEffect(() => {
    if (!panelOpen) return;
    const unbind = bindModelEvents();
    return unbind;
  }, [panelOpen, bindModelEvents]);

  // 面板开关时通知 viewer 进入/退出模型编辑模式，
  // 屏蔽/恢复 splat 点击导航避免与模型选中冲突
  useEffect(() => {
    const ctrl = getController();
    ctrl?.model.setEditMode?.(panelOpen);
    return () => {
      ctrl?.model.setEditMode?.(false);
    };
  }, [panelOpen, getController]);

  // gizmo 模式或选中模型变化时，通知 viewer 显示对应 gizmo
  useEffect(() => {
    const ctrl = getController();
    if (!panelOpen || !selectedAssetId) {
      // 面板关闭或取消选中时移除 gizmo
      ctrl?.model.setGizmo?.(selectedAssetId ?? '', null);
      return;
    }
    ctrl?.model.setGizmo?.(selectedAssetId, gizmoMode);
  }, [panelOpen, selectedAssetId, gizmoMode, getController]);

  // 选中模型时聚焦相机到模型位置（仅在 selectedAssetId 变化时触发，
  // 不在 panelOpen 变化时触发，避免面板重新打开时相机意外跳动）。
  // canvas 拾取选中不触发聚焦，避免点击模型时相机意外跳动。
  const selectedFromPick = useAssetLibraryStore((s) => s.selectedFromPick);
  useEffect(() => {
    if (!panelOpen || !selectedAssetId || selectedFromPick) return;
    const ctrl = getController();
    if (!ctrl) return;
    ctrl.model.focusModel?.(selectedAssetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId, selectedFromPick]);

  if (!panelOpen) return null;

  return (
    <div className="pointer-events-auto absolute top-4 right-4 z-40 flex items-start gap-2 select-none">
      {/* 左侧 gizmo 工具栏 */}
      <GizmoToolbar disabled={!selectedAssetId} />

      {/* 主面板：固定 80vh 高度，中间列表区域可滚动，其余区域固定 */}
      <div className="flex h-[80vh] w-80 flex-col overflow-hidden rounded-3xl bg-white/5 backdrop-blur-sm backdrop-brightness-30">
        {/* 标题栏 */}
        <div className="flex shrink-0 items-center justify-between px-4 py-3">
          <span className="text-sm font-medium text-white/90">{t('assetLibrary.title')}</span>
          <MtTooltip content={t('assetLibrary.close')} placement="bottom">
            <Button
              isIconOnly
              size="sm"
              variant="secondary"
              aria-label={t('assetLibrary.close')}
              onPress={() => setPanelOpen(false)}
              className="h-6 w-6 min-w-6 rounded-md bg-transparent p-0 text-white/50 hover:bg-white/10 hover:text-white/80"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="size-3.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          </MtTooltip>
        </div>

        {/* 导入区域 */}
        <div className="shrink-0 px-4 pb-3">
          <ImportDropZone
            onFile={(f) => void importFile(f)}
            onUrl={(u) => void importUrl(u)}
            onFiles={(fs) => void importGltfFiles(fs)}
          />
        </div>

        {/* 分割线 */}
        <div className="mx-4 h-px shrink-0 bg-white/10" />

        {/* 模型卡片网格（可滚动） */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <AssetCardGrid
            onRemove={removeAsset}
            onDuplicate={(id) => void duplicateAsset(id)}
            onRename={rename}
          />
        </div>

        {/* 分割线 */}
        <div className="mx-4 h-px shrink-0 bg-white/10" />

        {/* Transform 编辑器（同时显示位置/旋转/缩放） */}
        <div className="shrink-0 px-4 py-3">
          <TransformEditor onSetTransform={setTransform} />
        </div>
      </div>
    </div>
  );
}
